/**
 * CacheKeyFilter - Utility for filtering cache keys based on criteria
 * 
 * Extracted from TypesetterWorkerClient for better modularity and testability.
 * 
 * This utility provides functions for finding cache keys that match certain criteria,
 * which is useful for cache invalidation and cleanup operations.
 */

import type { PageLayout } from '../../../core/types/Typesetter';

/**
 * Find cache keys for pages that should be invalidated
 * 
 * Finds all cache keys in a page cache that match a docHash prefix and have
 * a page_index greater than or equal to the startPageIndex.
 * 
 * @param pageCache - Map of cache keys to PageLayout objects
 * @param docHash - Document hash prefix to match (e.g., "abc123")
 * @param startPageIndex - Minimum page index to invalidate (inclusive)
 * @returns Array of cache keys that should be deleted
 */
export function findCacheKeysToInvalidate(
  pageCache: Map<string, PageLayout>,
  docHash: string,
  startPageIndex: number
): string[] {
  const keysToDelete: string[] = [];
  
  for (const [key, page] of pageCache.entries()) {
    if (key.startsWith(docHash) && page.page_index >= startPageIndex) {
      keysToDelete.push(key);
    }
  }
  
  return keysToDelete;
}

/**
 * Find page indices in a hash map that should be invalidated
 * 
 * Finds all page indices in a page hashes map that are greater than or equal
 * to the startPageIndex.
 * 
 * @param pageHashes - Map from page_index to page_hash
 * @param startPageIndex - Minimum page index to invalidate (inclusive)
 * @returns Array of page indices that should be deleted
 */
export function findPageIndicesToInvalidate(
  pageHashes: Map<number, string>,
  startPageIndex: number
): number[] {
  const indicesToDelete: number[] = [];
  
  for (const [pageIndex] of pageHashes.entries()) {
    if (pageIndex >= startPageIndex) {
      indicesToDelete.push(pageIndex);
    }
  }
  
  return indicesToDelete;
}

/**
 * Filter cache keys by document hash prefix
 * 
 * Returns all cache keys that start with the given document hash prefix.
 * 
 * @param pageCache - Map of cache keys to PageLayout objects
 * @param docHash - Document hash prefix to match
 * @returns Array of cache keys matching the prefix
 */
export function filterCacheKeysByDocHash(
  pageCache: Map<string, PageLayout>,
  docHash: string
): string[] {
  const matchingKeys: string[] = [];
  
  for (const key of pageCache.keys()) {
    if (key.startsWith(docHash)) {
      matchingKeys.push(key);
    }
  }
  
  return matchingKeys;
}

